/**
 * Pure replenishment logic (CC-6: NEVER use lingui macros here).
 *
 * A rule (reorder_rule) defines min/max per product×warehouse. When
 * on-hand falls below the minimum, replenishment up to the maximum is
 * suggested (or up to the minimum if no maximum is defined). Deterministic, no AI.
 *
 * Quantities are in minor units (integers, factor 1000), same as F1.
 */

export interface ReorderRuleLike {
  id: string;
  product_id: string | null;
  warehouse_id: string | null;
  min_qty: number | null;
  max_qty: number | null;
}

export interface ReplenishmentSuggestion {
  ruleId: string;
  productId: string;
  warehouseId: string | null;
  onHand: number;
  minQty: number;
  maxQty: number;
  /** Quantity to replenish (> 0). target − onHand. */
  suggestedQty: number;
}

/**
 * Calculates the suggested quantity for a rule given the current on-hand. Returns 0
 * if stock is at or above the minimum (no replenishment is needed). The
 * target is the maximum; if the maximum is <= the minimum, replenish up to the minimum.
 */
export function computeSuggestedQty(onHand: number, minQty: number, maxQty: number): number {
  if (onHand >= minQty) {
    return 0;
  }
  const target = maxQty > minQty ? maxQty : minQty;
  const suggested = target - onHand;
  return Math.max(suggested, 0);
}

/**
 * Generates the list of replenishment suggestions from the rules and
 * an on-hand map by (product, warehouse). On-hand is resolved by warehouse
 * when the rule sets warehouse_id; if the rule is global (no warehouse), it uses
 * the product's total on-hand.
 *
 * @param onHandByKey  key `${productId} ${warehouseId ?? ""}` → on-hand.
 * @param onHandByProduct  productId → total on-hand (fallback for global rules).
 */
export function computeReplenishmentSuggestions(
  rules: ReorderRuleLike[],
  onHandByKey: Map<string, number>,
  onHandByProduct: Map<string, number>,
): ReplenishmentSuggestion[] {
  const suggestions: ReplenishmentSuggestion[] = [];
  for (const rule of rules) {
    const productId = rule.product_id;
    if (!productId) {
      continue;
    }
    const minQty = rule.min_qty ?? 0;
    const maxQty = rule.max_qty ?? 0;
    const onHand = rule.warehouse_id
      ? (onHandByKey.get(`${productId} ${rule.warehouse_id}`) ?? 0)
      : (onHandByProduct.get(productId) ?? 0);
    const suggestedQty = computeSuggestedQty(onHand, minQty, maxQty);
    if (suggestedQty > 0) {
      suggestions.push({
        ruleId: rule.id,
        productId,
        warehouseId: rule.warehouse_id,
        onHand,
        minQty,
        maxQty,
        suggestedQty,
      });
    }
  }
  return suggestions;
}
